import React, { useCallback, useEffect, useState } from "react";
import axios from "../utils/api.js";
import { useLocation, useNavigate } from "react-router-dom";

const ADDON_CURRENCIES = ["INR", "USD", "EUR", "GBP", "CAD", "AUD", "SGD", "JPY"];

const emptyAddonCreateForm = () => ({
  planName: "",
  price: { amount: "", currency: "INR", discount: 0 },
  planValidity: 30,
  totalHoursPerMonth: "",
  isUnlimited: false,
  isAddOn: true,
  autoPayEnabled: false,
  autoPayPeriodMonths: 1,
  freeMonths: 0,
  description: "",
});

const DEFAULT_LIMIT = 10;

const computeFinalPrice = (amount, discount) => {
  const a = Number(amount) || 0;
  const d = Number(discount) || 0;
  return Math.round(a - d);
};

const displayAddonFinalPrice = (plan) => {
  const price = plan?.price;
  if (!price) return 0;
  if (price.finalPrice != null && price.finalPrice !== "") return Number(price.finalPrice);
  return computeFinalPrice(price.amount, price.discount);
};

const sanitizeAddonUpdatePayload = (data) => {
  const p = JSON.parse(JSON.stringify(data));
  delete p._id;
  delete p.__v;
  delete p.createdAt;
  delete p.updatedAt;
  delete p.razorpayPlanId;
  delete p.status;
  return p;
};

const Snackbar = ({ message, open, onClose, type = "error" }) => (
  <div
    style={{
      position: "fixed",
      bottom: open ? "24px" : "-100px",
      left: "50%",
      transform: "translateX(-50%)",
      backgroundColor: type === "success" ? "#22c55e" : "#ff4444",
      color: "white",
      padding: "12px 24px",
      borderRadius: "8px",
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      zIndex: 1000,
      transition: "bottom 0.3s ease-in-out",
      maxWidth: "90%",
      width: "auto",
      minWidth: "300px",
    }}
  >
    <span style={{ flex: 1 }}>{message}</span>
    <button
      onClick={onClose}
      style={{
        background: "transparent",
        border: "none",
        color: "white",
        fontSize: "20px",
        cursor: "pointer",
        marginLeft: "16px",
        padding: "0 8px",
        borderRadius: "50%",
        width: "28px",
        height: "28px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      aria-label="Close notification"
    >
      &times;
    </button>
  </div>
);

const inp = {
  width: "100%",
  padding: "8px 10px",
  border: "1px solid #e2e8f0",
  borderRadius: "8px",
  fontSize: "14px",
  boxSizing: "border-box",
};

const CreateAddOnDialog = ({ open, onClose, onCreated, setSnackbar }) => {
  const [form, setForm] = useState(emptyAddonCreateForm);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (open) setForm(emptyAddonCreateForm());
  }, [open]);

  const calcFinal = () => {
    const amt = Number(form.price.amount);
    if (!amt || Number.isNaN(amt)) return 0;
    return Math.round(amt - (Number(form.price.discount) || 0));
  };

  const update = (path, value) => {
    if (path.startsWith("price.")) {
      const key = path.replace("price.", "");
      setForm((prev) => ({
        ...prev,
        price: { ...prev.price, [key]: value },
      }));
      return;
    }
    setForm((prev) => ({ ...prev, [path]: value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const name = (form.planName || "").trim();
    if (!name) {
      setSnackbar({ open: true, message: "Plan name is required", type: "error" });
      return;
    }
    const amt = Number(form.price.amount);
    if (!amt || amt <= 0) {
      setSnackbar({
        open: true,
        message: "Price amount must be greater than 0",
        type: "error",
      });
      return;
    }
    if (calcFinal() <= 0) {
      setSnackbar({
        open: true,
        message: "Final price (amount − discount) must be greater than 0",
        type: "error",
      });
      return;
    }
    const hours = Number(form.totalHoursPerMonth);
    if (!hours || hours <= 0) {
      setSnackbar({
        open: true,
        message: "Total hours per month must be greater than 0",
        type: "error",
      });
      return;
    }

    setCreating(true);
    try {
      const payload = {
        ...form,
        planName: name,
        price: {
          ...form.price,
          amount: amt,
          discount: Number(form.price.discount) || 0,
          finalPrice: calcFinal(),
        },
        totalHoursPerMonth: hours,
        planValidity: Number(form.planValidity) || 30,
      };
      await axios.post("/create-addonplan", payload);
      setSnackbar({
        open: true,
        message: "Add-on plan created successfully",
        type: "success",
      });
      onCreated();
      onClose();
    } catch (err) {
      setSnackbar({
        open: true,
        message:
          err.response?.data?.message ||
          err.response?.data?.error ||
          "Failed to create add-on plan",
        type: "error",
      });
    } finally {
      setCreating(false);
    }
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-addon-title"
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(15, 23, 42, 0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 2100,
        padding: "16px",
      }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          background: "white",
          borderRadius: "12px",
          maxWidth: "560px",
          width: "100%",
          maxHeight: "92vh",
          overflowY: "auto",
          boxShadow: "0 25px 50px rgba(0,0,0,0.15)",
          border: "1px solid #e2e8f0",
        }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "16px 20px",
            borderBottom: "1px solid #f1f5f9",
            position: "sticky",
            top: 0,
            background: "white",
            zIndex: 1,
          }}
        >
          <h2
            id="create-addon-title"
            style={{ margin: 0, fontSize: "18px", fontWeight: "700", color: "#1e293b" }}
          >
            Create add-on plan
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              background: "transparent",
              border: "none",
              fontSize: "22px",
              cursor: "pointer",
              color: "#64748b",
              lineHeight: 1,
            }}
          >
            &times;
          </button>
        </div>

        <form onSubmit={submit} style={{ padding: "20px", display: "flex", flexDirection: "column", gap: "14px" }}>
          <p style={{ fontSize: "12px", color: "#64748b", margin: 0, lineHeight: 1.5 }}>
            <strong>finalPrice</strong> is stored as amount minus fixed discount. Razorpay plan uses{" "}
            <strong>amount</strong> in paise, not finalPrice.
          </p>

          <div>
            <label style={{ fontSize: "12px", fontWeight: "600", color: "#475569", display: "block", marginBottom: "4px" }}>
              Plan name *
            </label>
            <input
              style={inp}
              value={form.planName}
              onChange={(e) => update("planName", e.target.value)}
              placeholder="e.g. AddOn-5Hours"
              required
            />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
            <div>
              <label style={{ fontSize: "12px", fontWeight: "600", color: "#475569", display: "block", marginBottom: "4px" }}>
                Amount *
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                style={inp}
                value={form.price.amount}
                onChange={(e) => update("price.amount", e.target.value === "" ? "" : Number(e.target.value))}
                required
              />
            </div>
            <div>
              <label style={{ fontSize: "12px", fontWeight: "600", color: "#475569", display: "block", marginBottom: "4px" }}>
                Currency
              </label>
              <select style={inp} value={form.price.currency} onChange={(e) => update("price.currency", e.target.value)}>
                {ADDON_CURRENCIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
            <div>
              <label style={{ fontSize: "12px", fontWeight: "600", color: "#475569", display: "block", marginBottom: "4px" }}>
                Discount (fixed)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                style={inp}
                value={form.price.discount}
                onChange={(e) => update("price.discount", e.target.value === "" ? 0 : Number(e.target.value))}
              />
            </div>
            <div>
              <label style={{ fontSize: "12px", fontWeight: "600", color: "#475569", display: "block", marginBottom: "4px" }}>
                Final price
              </label>
              <input style={{ ...inp, background: "#f8fafc" }} readOnly value={calcFinal()} />
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
            <div>
              <label style={{ fontSize: "12px", fontWeight: "600", color: "#475569", display: "block", marginBottom: "4px" }}>
                Hours/month *
              </label>
              <input
                type="number"
                min="1"
                style={inp}
                value={form.totalHoursPerMonth}
                onChange={(e) =>
                  update("totalHoursPerMonth", e.target.value === "" ? "" : Number(e.target.value))
                }
                required
              />
            </div>
            <div>
              <label style={{ fontSize: "12px", fontWeight: "600", color: "#475569", display: "block", marginBottom: "4px" }}>
                Validity (days)
              </label>
              <input
                type="number"
                min="1"
                style={inp}
                value={form.planValidity}
                onChange={(e) => update("planValidity", Number(e.target.value))}
              />
            </div>
          </div>

          <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
            <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", fontSize: "14px" }}>
              <input type="checkbox" checked={form.isUnlimited} onChange={(e) => update("isUnlimited", e.target.checked)} />
              Unlimited
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", fontSize: "14px" }}>
              <input
                type="checkbox"
                checked={form.autoPayEnabled}
                onChange={(e) => update("autoPayEnabled", e.target.checked)}
              />
              Auto-pay
            </label>
          </div>

          {form.autoPayEnabled && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
              <div>
                <label style={{ fontSize: "12px", fontWeight: "600", color: "#475569", display: "block", marginBottom: "4px" }}>
                  Auto-pay period (mo)
                </label>
                <input
                  type="number"
                  min="1"
                  style={inp}
                  value={form.autoPayPeriodMonths}
                  onChange={(e) => update("autoPayPeriodMonths", Number(e.target.value))}
                />
              </div>
              <div>
                <label style={{ fontSize: "12px", fontWeight: "600", color: "#475569", display: "block", marginBottom: "4px" }}>
                  Free months
                </label>
                <input
                  type="number"
                  min="0"
                  style={inp}
                  value={form.freeMonths}
                  onChange={(e) => update("freeMonths", Number(e.target.value))}
                />
              </div>
            </div>
          )}

          <div>
            <label style={{ fontSize: "12px", fontWeight: "600", color: "#475569", display: "block", marginBottom: "4px" }}>
              Description
            </label>
            <textarea
              style={{ ...inp, minHeight: "72px", resize: "vertical", fontFamily: "inherit" }}
              value={form.description}
              onChange={(e) => update("description", e.target.value)}
              rows={3}
            />
          </div>

          <div
            style={{
              display: "flex",
              gap: "10px",
              justifyContent: "flex-end",
              marginTop: "8px",
              paddingTop: "12px",
              borderTop: "1px solid #f1f5f9",
            }}
          >
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: "10px 18px",
                borderRadius: "8px",
                border: "1px solid #e2e8f0",
                background: "#f8fafc",
                fontWeight: "600",
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={creating}
              style={{
                padding: "10px 18px",
                borderRadius: "8px",
                border: "none",
                background: "#6366f1",
                color: "white",
                fontWeight: "600",
                cursor: creating ? "not-allowed" : "pointer",
                opacity: creating ? 0.85 : 1,
              }}
            >
              {creating ? "Creating…" : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const AddOnPlansList = () => {
  const [plans, setPlans] = useState([]);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: DEFAULT_LIMIT,
    totalPages: 1,
  });
  const [filterPlanNameDraft, setFilterPlanNameDraft] = useState("");
  const [filterStatusDraft, setFilterStatusDraft] = useState("");
  const [appliedPlanName, setAppliedPlanName] = useState("");
  const [appliedStatus, setAppliedStatus] = useState("");
  const [listMessage, setListMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", type: "error" });
  const [editingMongoId, setEditingMongoId] = useState(null);
  const [editData, setEditData] = useState({});
  const [deleting, setDeleting] = useState(null);
  const [statusUpdating, setStatusUpdating] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const fetchPlans = useCallback(async () => {
    setLoading(true);
    setListMessage("");
    const params = new URLSearchParams();
    params.set("page", String(pagination.page));
    params.set("limit", String(pagination.limit));
    if (appliedPlanName.trim()) params.set("planName", appliedPlanName.trim());
    if (appliedStatus) params.set("status", appliedStatus);

    try {
      const res = await axios.get(`/get-addonplans?${params.toString()}`);
      const rows = res.data?.data;
      setPlans(Array.isArray(rows) ? rows : []);
      const pg = res.data?.pagination;
      if (pg && typeof pg === "object") {
        setPagination((prev) => ({
          ...prev,
          total: Number(pg.total) || 0,
          page: Number(pg.page) || prev.page,
          limit: Number(pg.limit) || prev.limit,
          totalPages: Math.max(1, Number(pg.totalPages) || 1),
        }));
      }
    } catch (error) {
      if (error.response?.status === 404) {
        setPlans([]);
        setListMessage(
          error.response?.data?.message || "No add-on plans found"
        );
        setPagination((prev) => ({
          ...prev,
          total: 0,
          totalPages: 1,
        }));
      } else {
        setSnackbar({
          open: true,
          message:
            error.response?.data?.message ||
            error.response?.data?.error ||
            "Failed to load add-on plans",
          type: "error",
        });
      }
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, appliedPlanName, appliedStatus]);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  useEffect(() => {
    if (!location.state?.openCreateAddOn) return;
    setCreateDialogOpen(true);
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state?.openCreateAddOn, location.pathname, navigate]);

  const applyFilters = () => {
    setAppliedPlanName(filterPlanNameDraft.trim());
    setAppliedStatus(filterStatusDraft);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleEdit = (plan) => {
    setEditingMongoId(plan._id);
    setEditData(JSON.parse(JSON.stringify(plan)));
  };

  const handleSaveEdit = async () => {
    const name = (editData.planName || "").trim();
    if (!name) {
      setSnackbar({ open: true, message: "Plan name is required", type: "error" });
      return;
    }
    const hours = Number(editData.totalHoursPerMonth);
    if (!hours || hours <= 0) {
      setSnackbar({
        open: true,
        message: "Total hours per month must be greater than 0",
        type: "error",
      });
      return;
    }

    try {
      const payload = sanitizeAddonUpdatePayload(editData);
      payload.planName = name;
      if (payload.price) {
        const amount = Number(payload.price.amount) || 0;
        const discount = Number(payload.price.discount) || 0;
        payload.price = {
          ...payload.price,
          amount,
          discount,
          finalPrice: computeFinalPrice(amount, discount),
        };
      }
      payload.totalHoursPerMonth = hours;

      const res = await axios.put(`/put/addon/plan/${editingMongoId}`, payload);
      const updated = res.data?.data;
      if (updated) {
        setPlans((prev) =>
          prev.map((p) => (p._id === editingMongoId ? updated : p))
        );
      }
      setEditingMongoId(null);
      setEditData({});
      setSnackbar({ open: true, message: "Plan updated successfully", type: "success" });
      fetchPlans();
    } catch (error) {
      setSnackbar({
        open: true,
        message:
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Failed to update plan",
        type: "error",
      });
    }
  };

  /** @param {string} planMongoId document `_id` for route `:id` */
  const handleDelete = async (planMongoId) => {
    if (!window.confirm("Are you sure you want to delete this plan?")) return;
    setDeleting(planMongoId);
    try {
      await axios.delete(`/delete/addon/plan/${planMongoId}`);
      setPlans((prev) => prev.filter((p) => p._id !== planMongoId));
      setSnackbar({ open: true, message: "Plan deleted successfully", type: "success" });
      fetchPlans();
    } catch (error) {
      setSnackbar({
        open: true,
        message:
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Failed to delete plan",
        type: "error",
      });
    } finally {
      setDeleting(null);
    }
  };

  /**
   * @param {string} planMongoId document `_id` (route `:planId`)
   * @param {"draft"|"active"|"inactive"} status
   */
  const handleStatusChange = async (planMongoId, status) => {
    setStatusUpdating(planMongoId);
    try {
      await axios.patch(`/update-addonplan-status/${planMongoId}`, { status });
      setSnackbar({ open: true, message: "Add-on plan status updated", type: "success" });
      fetchPlans();
    } catch (error) {
      setSnackbar({
        open: true,
        message:
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Failed to update status",
        type: "error",
      });
    } finally {
      setStatusUpdating(null);
    }
  };

  const handleEditChange = (field, value) => {
    setEditData((prev) => {
      const next = { ...prev, [field]: value };

      if (field === "price") {
        const amount = Number(value?.amount ?? prev.price?.amount) || 0;
        const discount = Number(value?.discount ?? prev.price?.discount) || 0;
        next.price = {
          ...value,
          amount,
          discount,
          finalPrice: computeFinalPrice(amount, discount),
        };
      }

      return next;
    });
  };

  const goPage = (nextPage) => {
    const clamped = Math.min(
      pagination.totalPages,
      Math.max(1, nextPage)
    );
    setPagination((prev) => ({ ...prev, page: clamped }));
  };

  const DetailItem = ({ label, value }) => (
    <div>
      <div style={{ fontSize: "12px", color: "#64748b", marginBottom: "4px" }}>{label}</div>
      <div style={{ fontWeight: "600", color: "#1e293b" }}>{value}</div>
    </div>
  );

  const EditForm = ({ plan }) => {
    const displayStatus = editData.status ?? plan.status ?? "draft";
    return (
      <div>
        <h3
          style={{
            fontSize: "18px",
            fontWeight: "700",
            color: "#1e293b",
            marginBottom: "16px",
          }}
        >
          Edit Plan
        </h3>

        <div style={{ display: "grid", gap: "12px", marginBottom: "16px" }}>
          <div>
            <label
              style={{
                fontSize: "12px",
                color: "#64748b",
                marginBottom: "4px",
                display: "block",
              }}
            >
              Plan name *
            </label>
            <input
              type="text"
              value={editData.planName || ""}
              onChange={(e) => handleEditChange("planName", e.target.value)}
              style={{
                width: "100%",
                padding: "8px",
                border: "1px solid #e2e8f0",
                borderRadius: "6px",
                fontSize: "14px",
                boxSizing: "border-box",
              }}
            />
          </div>

          <div>
            <label
              style={{
                fontSize: "12px",
                color: "#64748b",
                marginBottom: "4px",
                display: "block",
              }}
            >
              Description
            </label>
            <textarea
              value={editData.description || ""}
              onChange={(e) => handleEditChange("description", e.target.value)}
              style={{
                width: "100%",
                padding: "8px",
                border: "1px solid #e2e8f0",
                borderRadius: "6px",
                fontSize: "14px",
                fontFamily: "inherit",
                minHeight: "80px",
                boxSizing: "border-box",
              }}
            />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
            <div>
              <label
                style={{
                  fontSize: "12px",
                  color: "#64748b",
                  marginBottom: "4px",
                  display: "block",
                }}
              >
                Price amount *
              </label>
              <input
                type="number"
                value={editData.price?.amount ?? ""}
                onChange={(e) =>
                  handleEditChange("price", {
                    ...editData.price,
                    amount: Number(e.target.value),
                  })
                }
                style={{
                  width: "100%",
                  padding: "8px",
                  border: "1px solid #e2e8f0",
                  borderRadius: "6px",
                  fontSize: "14px",
                  boxSizing: "border-box",
                }}
              />
            </div>

            <div>
              <label
                style={{
                  fontSize: "12px",
                  color: "#64748b",
                  marginBottom: "4px",
                  display: "block",
                }}
              >
                Currency
              </label>
              <select
                value={editData.price?.currency || "INR"}
                onChange={(e) =>
                  handleEditChange("price", {
                    ...editData.price,
                    currency: e.target.value,
                  })
                }
                style={{
                  width: "100%",
                  padding: "8px",
                  border: "1px solid #e2e8f0",
                  borderRadius: "6px",
                  fontSize: "14px",
                  boxSizing: "border-box",
                }}
              >
                {["INR", "USD", "EUR", "GBP", "CAD", "AUD", "SGD", "JPY"].map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
            <div>
              <label
                style={{
                  fontSize: "12px",
                  color: "#64748b",
                  marginBottom: "4px",
                  display: "block",
                }}
              >
                Discount (fixed)
              </label>
              <input
                type="number"
                value={editData.price?.discount ?? 0}
                onChange={(e) =>
                  handleEditChange("price", {
                    ...editData.price,
                    discount: Number(e.target.value),
                  })
                }
                style={{
                  width: "100%",
                  padding: "8px",
                  border: "1px solid #e2e8f0",
                  borderRadius: "6px",
                  fontSize: "14px",
                  boxSizing: "border-box",
                }}
              />
            </div>
            <div>
              <label
                style={{
                  fontSize: "12px",
                  color: "#64748b",
                  marginBottom: "4px",
                  display: "block",
                }}
              >
                Final price (preview)
              </label>
              <input
                type="number"
                readOnly
                value={computeFinalPrice(
                  editData.price?.amount,
                  editData.price?.discount
                )}
                style={{
                  width: "100%",
                  padding: "8px",
                  border: "1px solid #e2e8f0",
                  borderRadius: "6px",
                  fontSize: "14px",
                  boxSizing: "border-box",
                  background: "#f8fafc",
                }}
              />
            </div>
          </div>

          <div>
            <label
              style={{
                fontSize: "12px",
                color: "#64748b",
                marginBottom: "4px",
                display: "block",
              }}
            >
              Razorpay plan ID (read-only)
            </label>
            <div
              style={{
                padding: "8px",
                border: "1px solid #e2e8f0",
                borderRadius: "6px",
                fontSize: "13px",
                fontFamily: "ui-monospace, monospace",
                background: "#f8fafc",
                wordBreak: "break-all",
              }}
            >
              {(plan.razorpayPlanId || "").trim() || "—"}
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
            <div>
              <label
                style={{
                  fontSize: "12px",
                  color: "#64748b",
                  marginBottom: "4px",
                  display: "block",
                }}
              >
                Plan validity (days)
              </label>
              <input
                type="number"
                value={editData.planValidity ?? 30}
                onChange={(e) =>
                  handleEditChange("planValidity", Number(e.target.value))
                }
                style={{
                  width: "100%",
                  padding: "8px",
                  border: "1px solid #e2e8f0",
                  borderRadius: "6px",
                  fontSize: "14px",
                  boxSizing: "border-box",
                }}
              />
            </div>

            <div>
              <label
                style={{
                  fontSize: "12px",
                  color: "#64748b",
                  marginBottom: "4px",
                  display: "block",
                }}
              >
                Hours/month *
              </label>
              <input
                type="number"
                value={editData.totalHoursPerMonth ?? ""}
                onChange={(e) =>
                  handleEditChange("totalHoursPerMonth", Number(e.target.value))
                }
                style={{
                  width: "100%",
                  padding: "8px",
                  border: "1px solid #e2e8f0",
                  borderRadius: "6px",
                  fontSize: "14px",
                  boxSizing: "border-box",
                }}
              />
            </div>
          </div>

          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
            <label style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={!!editData.isUnlimited}
                onChange={(e) => handleEditChange("isUnlimited", e.target.checked)}
                style={{ cursor: "pointer" }}
              />
              <span style={{ fontSize: "14px" }}>Unlimited</span>
            </label>

            <label style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={!!editData.autoPayEnabled}
                onChange={(e) => handleEditChange("autoPayEnabled", e.target.checked)}
                style={{ cursor: "pointer" }}
              />
              <span style={{ fontSize: "14px" }}>Auto-pay</span>
            </label>
          </div>

          {editData.autoPayEnabled && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
              <div>
                <label
                  style={{
                    fontSize: "12px",
                    color: "#64748b",
                    marginBottom: "4px",
                    display: "block",
                  }}
                >
                  Auto-pay period (months)
                </label>
                <input
                  type="number"
                  value={editData.autoPayPeriodMonths ?? 1}
                  onChange={(e) =>
                    handleEditChange("autoPayPeriodMonths", Number(e.target.value))
                  }
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #e2e8f0",
                    borderRadius: "6px",
                    fontSize: "14px",
                    boxSizing: "border-box",
                  }}
                />
              </div>

              <div>
                <label
                  style={{
                    fontSize: "12px",
                    color: "#64748b",
                    marginBottom: "4px",
                    display: "block",
                  }}
                >
                  Free months
                </label>
                <input
                  type="number"
                  value={editData.freeMonths ?? 0}
                  onChange={(e) =>
                    handleEditChange("freeMonths", Number(e.target.value))
                  }
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #e2e8f0",
                    borderRadius: "6px",
                    fontSize: "14px",
                    boxSizing: "border-box",
                  }}
                />
              </div>
            </div>
          )}
        </div>

        <div style={{ marginBottom: "12px" }}>
          <label
            style={{
              fontSize: "12px",
              color: "#64748b",
              marginBottom: "4px",
              display: "block",
            }}
          >
            Status
          </label>
          <select
            value={displayStatus}
            disabled={statusUpdating === plan._id}
            onChange={(e) => {
              const next = e.target.value;
              if (next === displayStatus) return;
              handleEditChange("status", next);
              handleStatusChange(plan._id, next);
            }}
            style={{
              width: "100%",
              padding: "8px",
              border: "1px solid #e2e8f0",
              borderRadius: "6px",
              fontSize: "14px",
              boxSizing: "border-box",
            }}
          >
            <option value="draft">draft</option>
            <option value="active">active</option>
            <option value="inactive">inactive</option>
          </select>
        </div>

        <div style={{ display: "flex", gap: "12px" }}>
          <button
            type="button"
            onClick={handleSaveEdit}
            style={{
              flex: 1,
              padding: "10px 16px",
              backgroundColor: "#22c55e",
              color: "white",
              border: "none",
              borderRadius: "8px",
              cursor: "pointer",
              fontWeight: "600",
              fontSize: "14px",
            }}
          >
            Save
          </button>
          <button
            type="button"
            onClick={() => {
              setEditingMongoId(null);
              setEditData({});
            }}
            style={{
              flex: 1,
              padding: "10px 16px",
              backgroundColor: "#94a3b8",
              color: "white",
              border: "none",
              borderRadius: "8px",
              cursor: "pointer",
              fontWeight: "600",
              fontSize: "14px",
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    );
  };

  const PlanCard = ({ plan }) => {
    const final = displayAddonFinalPrice(plan);
    const discount = Number(plan.price?.discount) || 0;
    const status = plan.status || "draft";

    return (
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "16px",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.08)",
          padding: "32px",
          border: "1px solid #e2e8f0",
          minWidth: "320px",
          transition: "transform 0.2s ease, box-shadow 0.2s ease",
          position: "relative",
          overflow: "hidden",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = "translateY(-4px)";
          e.currentTarget.style.boxShadow = "0 12px 40px rgba(0, 0, 0, 0.12)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = "0 8px 32px rgba(0, 0, 0, 0.08)";
        }}
      >
        {editingMongoId === plan._id ? (
          <EditForm plan={plan} />
        ) : (
          <>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "12px",
                flexWrap: "wrap",
                gap: "8px",
              }}
            >
              <span
                style={{
                  fontSize: "11px",
                  fontWeight: "700",
                  textTransform: "uppercase",
                  padding: "4px 10px",
                  borderRadius: "999px",
                  background:
                    status === "active"
                      ? "#dcfce7"
                      : status === "inactive"
                        ? "#fee2e2"
                        : "#e2e8f0",
                  color:
                    status === "active"
                      ? "#166534"
                      : status === "inactive"
                        ? "#991b1b"
                        : "#475569",
                }}
              >
                {status}
              </span>
            </div>

            {plan.isPopular && (
              <div
                style={{
                  position: "absolute",
                  top: "16px",
                  right: "16px",
                  backgroundColor: "#6366f1",
                  color: "white",
                  padding: "4px 12px",
                  borderRadius: "20px",
                  fontSize: "12px",
                  fontWeight: "600",
                }}
              >
                POPULAR
              </div>
            )}

            <h3
              style={{
                fontSize: "20px",
                fontWeight: "700",
                color: "#1e293b",
                marginBottom: "12px",
                paddingRight: plan.isPopular ? "70px" : "0",
              }}
            >
              {plan.planName}
            </h3>

            <div
              style={{
                marginBottom: "16px",
                color: "#64748b",
                lineHeight: "1.5",
              }}
            >
              {plan.description}
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "20px",
                flexWrap: "wrap",
                gap: "8px",
              }}
            >
              <div
                style={{
                  fontSize: "28px",
                  fontWeight: "800",
                  color: "#6366f1",
                }}
              >
                {plan.price?.currency || "INR"} {final}
              </div>

              {discount > 0 && (
                <div
                  style={{
                    padding: "4px 8px",
                    backgroundColor: "#dcfce7",
                    color: "#16a34a",
                    borderRadius: "6px",
                    fontSize: "12px",
                    fontWeight: "600",
                  }}
                >
                  −{plan.price?.currency || "INR"} {discount} fixed
                </div>
              )}
            </div>

            {discount > 0 && (
              <div
                style={{
                  textDecoration: "line-through",
                  color: "#94a3b8",
                  marginBottom: "8px",
                  fontSize: "14px",
                }}
              >
                {plan.price?.currency || "INR"} {plan.price?.amount}
              </div>
            )}

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "12px",
                marginTop: "20px",
                paddingTop: "20px",
                borderTop: "1px solid #f1f5f9",
              }}
            >
              <DetailItem label="Validity" value={`${plan.planValidity ?? "-"} days`} />
              <DetailItem label="Hours/month" value={plan.totalHoursPerMonth ?? "-"} />
              <DetailItem label="Unlimited" value={plan.isUnlimited ? "Yes" : "No"} />
              <DetailItem label="Auto-pay" value={plan.autoPayEnabled ? "Yes" : "No"} />
              {plan.autoPayEnabled && (
                <DetailItem label="Auto-pay period" value={`${plan.autoPayPeriodMonths} mo`} />
              )}
              <DetailItem label="Free months" value={plan.freeMonths ?? "-"} />
            </div>

            {plan.razorpayPlanId && (
              <div
                style={{
                  marginTop: "16px",
                  fontSize: "12px",
                  color: "#64748b",
                  wordBreak: "break-all",
                  fontFamily: "ui-monospace, monospace",
                }}
              >
                Razorpay: {plan.razorpayPlanId}
              </div>
            )}

            <div style={{ marginTop: "16px" }}>
              <label
                style={{
                  fontSize: "12px",
                  color: "#64748b",
                  marginBottom: "4px",
                  display: "block",
                }}
              >
                Status
              </label>
              <select
                value={status}
                disabled={statusUpdating === plan._id}
                onChange={(e) => {
                  const next = e.target.value;
                  if (next === status) return;
                  handleStatusChange(plan._id, next);
                }}
                style={{
                  width: "100%",
                  padding: "8px",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  fontSize: "14px",
                  boxSizing: "border-box",
                }}
              >
                <option value="draft">draft</option>
                <option value="active">active</option>
                <option value="inactive">inactive</option>
              </select>
            </div>

            <div
              style={{
                display: "flex",
                gap: "12px",
                marginTop: "24px",
                paddingTop: "20px",
                borderTop: "1px solid #f1f5f9",
              }}
            >
              <button
                type="button"
                onClick={() => handleEdit(plan)}
                style={{
                  flex: 1,
                  padding: "10px 16px",
                  backgroundColor: "#6366f1",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontWeight: "600",
                  fontSize: "14px",
                }}
              >
                Edit
              </button>
              <button
                type="button"
                onClick={() => handleDelete(plan._id)}
                disabled={deleting === plan._id}
                style={{
                  flex: 1,
                  padding: "10px 16px",
                  backgroundColor: deleting === plan._id ? "#fca5a5" : "#ef4444",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: deleting === plan._id ? "not-allowed" : "pointer",
                  fontWeight: "600",
                  fontSize: "14px",
                }}
              >
                {deleting === plan._id ? "Deleting..." : "Delete"}
              </button>
            </div>
          </>
        )}
      </div>
    );
  };

  if (loading && plans.length === 0 && !listMessage) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "60vh",
          flexDirection: "column",
          gap: "16px",
        }}
      >
        <div
          style={{
            width: "48px",
            height: "48px",
            borderRadius: "50%",
            border: "4px solid #f1f5f9",
            borderTopColor: "#6366f1",
            animation: "spin 1s linear infinite",
          }}
        />
        <p style={{ color: "#64748b" }}>Loading plans...</p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div
      style={{
        maxWidth: "1200px",
        margin: "0 auto",
        padding: "48px 24px",
        minHeight: "100vh",
      }}
    >
      <div style={{ marginBottom: "16px", display: "flex", gap: "12px", flexWrap: "wrap" }}>
        <button
          type="button"
          onClick={() => navigate(-1)}
          aria-label="Go back"
          style={{
            padding: "8px 12px",
            backgroundColor: "#94a3b8",
            color: "white",
            border: "none",
            borderRadius: "8px",
            cursor: "pointer",
            fontWeight: "600",
            fontSize: "14px",
          }}
        >
          ← Back
        </button>
        <button
          type="button"
          onClick={() => setCreateDialogOpen(true)}
          style={{
            padding: "8px 12px",
            backgroundColor: "#6366f1",
            color: "white",
            border: "none",
            borderRadius: "8px",
            cursor: "pointer",
            fontWeight: "600",
            fontSize: "14px",
          }}
        >
          + Create add-on
        </button>
      </div>
      <CreateAddOnDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onCreated={() => fetchPlans()}
        setSnackbar={setSnackbar}
      />
      <Snackbar
        message={snackbar.message}
        open={snackbar.open}
        type={snackbar.type}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />

      <header style={{ textAlign: "center", marginBottom: "32px" }}>
        <h1
          style={{
            fontSize: "36px",
            fontWeight: "800",
            color: "#1e293b",
            marginBottom: "12px",
            background: "linear-gradient(90deg, #6366f1, #8b5cf6)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          Add-On Plans
        </h1>
        <p
          style={{
            fontSize: "18px",
            color: "#64748b",
            maxWidth: "600px",
            margin: "0 auto",
            lineHeight: "1.6",
          }}
        >
          Filter, paginate, and manage lifecycle (draft / active / inactive)
        </p>
      </header>

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "12px",
          alignItems: "flex-end",
          marginBottom: "24px",
          padding: "16px",
          background: "#f8fafc",
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
        }}
      >
        <div>
          <label
            style={{
              display: "block",
              fontSize: "12px",
              color: "#64748b",
              marginBottom: "4px",
            }}
          >
            Plan name (exact)
          </label>
          <input
            type="text"
            value={filterPlanNameDraft}
            onChange={(e) => setFilterPlanNameDraft(e.target.value)}
            placeholder="Exact match"
            style={{
              padding: "8px 12px",
              borderRadius: "8px",
              border: "1px solid #e2e8f0",
              fontSize: "14px",
              minWidth: "180px",
            }}
          />
        </div>
        <div>
          <label
            style={{
              display: "block",
              fontSize: "12px",
              color: "#64748b",
              marginBottom: "4px",
            }}
          >
            Status
          </label>
          <select
            value={filterStatusDraft}
            onChange={(e) => setFilterStatusDraft(e.target.value)}
            style={{
              padding: "8px 12px",
              borderRadius: "8px",
              border: "1px solid #e2e8f0",
              fontSize: "14px",
            }}
          >
            <option value="">All</option>
            <option value="draft">draft</option>
            <option value="active">active</option>
            <option value="inactive">inactive</option>
          </select>
        </div>
        <div>
          <label
            style={{
              display: "block",
              fontSize: "12px",
              color: "#64748b",
              marginBottom: "4px",
            }}
          >
            Per page
          </label>
          <select
            value={pagination.limit}
            onChange={(e) =>
              setPagination((prev) => ({
                ...prev,
                limit: Number(e.target.value),
                page: 1,
              }))
            }
            style={{
              padding: "8px 12px",
              borderRadius: "8px",
              border: "1px solid #e2e8f0",
              fontSize: "14px",
            }}
          >
            {[10, 20, 50].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>
        <button
          type="button"
          onClick={applyFilters}
          style={{
            padding: "8px 16px",
            backgroundColor: "#6366f1",
            color: "white",
            border: "none",
            borderRadius: "8px",
            fontWeight: "600",
            cursor: "pointer",
            fontSize: "14px",
          }}
        >
          Apply filters
        </button>
      </div>

      {(plans.length === 0 && listMessage) || (plans.length === 0 && !loading) ? (
        <div
          style={{
            textAlign: "center",
            padding: "60px 24px",
            backgroundColor: "#f8fafc",
            borderRadius: "16px",
            color: "#64748b",
            margin: "0 auto",
            maxWidth: "600px",
          }}
        >
          <div style={{ fontSize: "48px", marginBottom: "16px" }}>📋</div>
          <h3 style={{ marginBottom: "12px", color: "#475569" }}>
            {listMessage || "No plans available"}
          </h3>
          <p>Adjust filters or create a new add-on plan.</p>
        </div>
      ) : (
        <>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "12px",
              marginBottom: "16px",
            }}
          >
            <span style={{ fontSize: "14px", color: "#64748b" }}>
              Page {pagination.page} of {pagination.totalPages} · {pagination.total} total
            </span>
            <div style={{ display: "flex", gap: "8px" }}>
              <button
                type="button"
                disabled={pagination.page <= 1 || loading}
                onClick={() => goPage(pagination.page - 1)}
                style={{
                  padding: "8px 14px",
                  borderRadius: "8px",
                  border: "1px solid #e2e8f0",
                  background: pagination.page <= 1 ? "#f1f5f9" : "white",
                  cursor: pagination.page <= 1 ? "not-allowed" : "pointer",
                  fontWeight: "600",
                }}
              >
                Previous
              </button>
              <button
                type="button"
                disabled={pagination.page >= pagination.totalPages || loading}
                onClick={() => goPage(pagination.page + 1)}
                style={{
                  padding: "8px 14px",
                  borderRadius: "8px",
                  border: "1px solid #e2e8f0",
                  background:
                    pagination.page >= pagination.totalPages ? "#f1f5f9" : "white",
                  cursor:
                    pagination.page >= pagination.totalPages ? "not-allowed" : "pointer",
                  fontWeight: "600",
                }}
              >
                Next
              </button>
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(350px, 1fr))",
              gap: "32px",
            }}
          >
            {plans.map((plan) => (
              <PlanCard key={plan._id} plan={plan} />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default AddOnPlansList;
